import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TMUA_PEARSON_TOKENS, getTmuaPearsonChrome } from '../constants/tmuaPearsonTokens';
import { getYesNoRestWidth, getYesNoShrinkWidths, getYesNoTypeScale } from '../utils/yesNoShrinkLayout';

function isYesNoGhostCoarse() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(pointer: coarse)').matches
    || window.matchMedia('(max-width: 768px)').matches;
}

/**
 * Pearson-VUE-replica drag-and-drop Yes/No widget for UCAT Decision Making
 * yes_no_group questions. Drag a "Yes"/"No" tile from the side tray onto a
 * drop zone next to each statement; drop onto an occupied zone overwrites it;
 * dragging a placed tile off any zone clears it. Mouse and touch share one
 * Pointer Events code path — REPLICA.md documents the live exam as mouse
 * only, so there is no keyboard placement path, only aria-labels for
 * screen-reader readability of state.
 */
export default function TmuaPearsonDragYesNo({
  items,
  value,
  onPlace,
  onClear,
  readOnly,
  getCorrectness,
  colorScheme,
  prepareExamHtml,
  questionTextStyle,
  splitItem,
  questionId,
}) {
  const T = TMUA_PEARSON_TOKENS;
  const chrome = getTmuaPearsonChrome(colorScheme);
  const dropzoneEmptyBg = chrome.ynDropzoneEmptyBg || T.ynDropzoneEmptyBg;
  const trayBg = chrome.ynTrayBg || T.ynTrayBg;
  const restPx = getYesNoRestWidth(T);

  const rowsRef = useRef(null);
  const boxRefs = useRef({});
  const dragRef = useRef(null);
  const paneScrollRef = useRef(null);
  const autoScrollRafRef = useRef(null);
  const [ghost, setGhost] = useState(null); // { x, y, val, width, height, transform } | null
  const ghostRafRef = useRef(null);
  const pendingGhostRef = useRef(null);
  const shrinkRafRef = useRef(null);

  const list = items || [];

  const isFilled = useCallback((itemId) => Object.prototype.hasOwnProperty.call(value || {}, String(itemId)), [value]);
  const valueOf = useCallback((itemId) => (value || {})[String(itemId)], [value]);

  const scheduleGhostRender = useCallback(() => {
    if (ghostRafRef.current) return;
    ghostRafRef.current = requestAnimationFrame(() => {
      ghostRafRef.current = null;
      if (pendingGhostRef.current) setGhost({ ...pendingGhostRef.current });
    });
  }, []);

  const resolvePane = useCallback(() => {
    if (paneScrollRef.current) return paneScrollRef.current;
    let el = rowsRef.current;
    while (el && el !== document.body) {
      if (el.classList && el.classList.contains('ivori-exam-question-pane')) {
        paneScrollRef.current = el;
        return el;
      }
      el = el.parentElement;
    }
    return null;
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (autoScrollRafRef.current) {
      cancelAnimationFrame(autoScrollRafRef.current);
      autoScrollRafRef.current = null;
    }
  }, []);

  const maybeAutoScroll = useCallback((clientY) => {
    const pane = resolvePane();
    if (!pane) return;
    const rect = pane.getBoundingClientRect();
    const edge = T.ynAutoScrollEdgePx;
    const distTop = clientY - rect.top;
    const distBottom = rect.bottom - clientY;
    let speed = 0;
    if (distTop >= 0 && distTop < edge) {
      speed = -Math.round((edge - distTop) / edge * 12);
    } else if (distBottom >= 0 && distBottom < edge) {
      speed = Math.round((edge - distBottom) / edge * 12);
    }
    stopAutoScroll();
    if (speed !== 0) {
      const step = () => {
        pane.scrollTop += speed;
        autoScrollRafRef.current = requestAnimationFrame(step);
      };
      autoScrollRafRef.current = requestAnimationFrame(step);
    }
  }, [T.ynAutoScrollEdgePx, resolvePane, stopAutoScroll]);

  const handlersRef = useRef({});

  const endDrag = useCallback(() => {
    stopAutoScroll();
    const d = dragRef.current;
    if (d && d.captureEl) {
      if (d.onMove) d.captureEl.removeEventListener('pointermove', d.onMove);
      if (d.onUp) d.captureEl.removeEventListener('pointerup', d.onUp);
      if (d.onCancel) d.captureEl.removeEventListener('pointercancel', d.onCancel);
      if (d.pointerId != null && typeof d.captureEl.releasePointerCapture === 'function') {
        try {
          if (!d.captureEl.hasPointerCapture || d.captureEl.hasPointerCapture(d.pointerId)) {
            d.captureEl.releasePointerCapture(d.pointerId);
          }
        } catch (err) { /* ignore */ }
      }
    }
    dragRef.current = null;
    pendingGhostRef.current = null;
    setGhost(null);
    if (typeof document !== 'undefined') document.body.style.cursor = '';
  }, [stopAutoScroll]);

  const onPointerMoveWindow = useCallback((e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    pendingGhostRef.current = {
      x: e.clientX,
      y: e.clientY,
      val: d.value,
      width: d.tileWidth,
      height: d.tileHeight,
      transform: d.ghostTransform,
    };
    scheduleGhostRender();
    if (!d.started) {
      const dx = e.clientX - d.originX;
      const dy = e.clientY - d.originY;
      if (Math.hypot(dx, dy) < T.ynDragThresholdPx) return;
      d.started = true;
    }
    e.preventDefault();
    maybeAutoScroll(e.clientY);
  }, [T.ynDragThresholdPx, scheduleGhostRender, maybeAutoScroll]);

  const onPointerUpWindow = useCallback((e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    if (d.started) {
      const el = typeof document !== 'undefined' ? document.elementFromPoint(e.clientX, e.clientY) : null;
      const dropzoneEl = el && el.closest ? el.closest('[data-yn-dropzone-item]') : null;
      const targetItemId = dropzoneEl ? dropzoneEl.getAttribute('data-yn-dropzone-item') : null;
      if (targetItemId) {
        onPlace(targetItemId, d.value);
        if (d.source === 'box' && d.fromItemId && d.fromItemId !== targetItemId) {
          onClear(d.fromItemId);
        }
      } else if (d.source === 'box') {
        onClear(d.fromItemId);
      }
    }
    endDrag();
  }, [onPlace, onClear, endDrag]);

  const onPointerCancelWindow = useCallback((e) => {
    const d = dragRef.current;
    if (!d || e.pointerId !== d.pointerId) return;
    endDrag();
  }, [endDrag]);

  handlersRef.current = { onPointerMoveWindow, onPointerUpWindow, onPointerCancelWindow };

  useEffect(() => {
    const onMove = (e) => handlersRef.current.onPointerMoveWindow(e);
    const onUp = (e) => handlersRef.current.onPointerUpWindow(e);
    const onCancel = (e) => handlersRef.current.onPointerCancelWindow(e);
    const optsMove = { capture: true, passive: false };
    const optsUp = { capture: true };
    document.addEventListener('pointermove', onMove, optsMove);
    document.addEventListener('pointerup', onUp, optsUp);
    document.addEventListener('pointercancel', onCancel, optsUp);
    window.addEventListener('pointermove', onMove, { passive: false });
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
    return () => {
      document.removeEventListener('pointermove', onMove, optsMove);
      document.removeEventListener('pointerup', onUp, optsUp);
      document.removeEventListener('pointercancel', onCancel, optsUp);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape' && dragRef.current) endDrag();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [endDrag]);

  useEffect(() => {
    const root = rowsRef.current;
    if (!root) return undefined;
    if (typeof ResizeObserver === 'undefined') return undefined;

    let observer = null;

    const applyWidths = () => {
      const available = root.clientWidth;
      if (available <= 0) return;
      const { statementWidth, dropWidth } = getYesNoShrinkWidths(available, T);
      const { statementFontSize, statementLineHeight, tileFontSize } = getYesNoTypeScale(
        { statementWidth, dropWidth },
        T,
      );
      root.style.setProperty('--yn-stmt-w', `${statementWidth}px`);
      root.style.setProperty('--dz-w', `${dropWidth}px`);
      root.style.setProperty('--yn-stmt-fs', `${statementFontSize}px`);
      root.style.setProperty('--yn-tile-fs', `${tileFontSize}px`);
      root.style.setProperty('--yn-stmt-lh', String(statementLineHeight));
    };

    const cancelShrinkRaf = () => {
      if (shrinkRafRef.current) {
        cancelAnimationFrame(shrinkRafRef.current);
        shrinkRafRef.current = null;
      }
    };

    observer = new ResizeObserver(() => {
      if (shrinkRafRef.current) return;
      shrinkRafRef.current = requestAnimationFrame(() => {
        shrinkRafRef.current = null;
        applyWidths();
      });
    });
    observer.observe(root);
    applyWidths();

    return () => {
      if (observer) observer.disconnect();
      cancelShrinkRaf();
    };
  }, [T, splitItem, questionId]);

  const startDrag = (e, { source, val, fromItemId }) => {
    if (readOnly) return;
    if (e.button !== undefined && e.button !== 0) return;
    const tileRect = e.currentTarget.getBoundingClientRect();
    const ghostTransform = isYesNoGhostCoarse() ? T.ynGhostTransformCoarse : T.ynGhostTransformFine;
    const captureEl = e.currentTarget;
    const ghostPayload = {
      x: e.clientX,
      y: e.clientY,
      val,
      width: tileRect.width,
      height: tileRect.height,
      transform: ghostTransform,
    };
    const onMove = (ev) => handlersRef.current.onPointerMoveWindow(ev);
    const onUp = (ev) => handlersRef.current.onPointerUpWindow(ev);
    const onCancel = (ev) => handlersRef.current.onPointerCancelWindow(ev);
    dragRef.current = {
      source,
      value: val,
      fromItemId: fromItemId || null,
      originX: e.clientX,
      originY: e.clientY,
      started: false,
      pointerId: e.pointerId,
      tileWidth: tileRect.width,
      tileHeight: tileRect.height,
      ghostTransform,
      captureEl,
      onMove,
      onUp,
      onCancel,
    };
    pendingGhostRef.current = ghostPayload;
    setGhost(ghostPayload);
    if (typeof document !== 'undefined') document.body.style.cursor = 'grabbing';
    if (captureEl && captureEl.addEventListener) {
      captureEl.addEventListener('pointermove', onMove, { passive: false });
      captureEl.addEventListener('pointerup', onUp);
      captureEl.addEventListener('pointercancel', onCancel);
    }
    if (captureEl && captureEl.setPointerCapture) {
      try { captureEl.setPointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    }
  };

  const renderTile = (val, { key, source, fromItemId, correctness, ariaLabel } = {}) => {
    const label = val ? 'Yes' : 'No';
    let border = T.ynTileBorder;
    let bg = T.ynTileBg;
    if (readOnly && correctness !== undefined && correctness !== null) {
      border = correctness ? T.pillCorrect : T.pillIncorrect;
      bg = correctness ? T.pillCorrectBg : T.pillIncorrectBg;
    }
    return (
      <div
        key={key}
        className="pearson-dnd-yn-tile"
        onPointerDown={readOnly ? undefined : (e) => startDrag(e, { source, val, fromItemId })}
        aria-label={ariaLabel}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: bg,
          border: `1px solid ${border}`,
          color: T.ynTileText,
          fontFamily: T.fontUi,
          fontWeight: 400,
          userSelect: 'none',
          touchAction: readOnly ? undefined : 'none',
          cursor: readOnly ? 'default' : 'grab',
        }}
      >
        {label}
      </div>
    );
  };

  const rowCount = list.length;
  const trayAlignRows = rowCount >= 4;

  return (
    <div
      ref={rowsRef}
      className={`pearson-dnd-yn-root${splitItem ? ' pearson-dnd-yn-root--split' : ''}`}
      style={{
        width: splitItem ? '100%' : restPx,
        maxWidth: splitItem ? 'none' : '100%',
        overflowX: 'hidden',
        display: 'flex',
        flexDirection: 'row',
        gap: T.ynTrayGap,
        position: 'relative',
        boxSizing: 'border-box',
      }}
    >
      <style>{`
        .pearson-dnd-yn-root {
          position: relative;
          --yn-stmt-w: ${T.ynStatementRestWidth}px;
          --dz-w: ${T.ynDropzoneWidth}px;
          --dz-h: ${T.ynDropzoneHeight}px;
          --yn-stmt-fs: ${T.ynStatementFontRest}px;
          --yn-stmt-lh: ${T.ynStatementLineHeightRest};
          --yn-tile-fs: ${T.ynTileFontRest}px;
        }
        .pearson-dnd-yn-dropzone, .pearson-dnd-yn-tile {
          width: var(--dz-w);
          height: var(--dz-h);
          flex: 0 0 var(--dz-w);
          box-sizing: border-box;
        }
        .pearson-dnd-yn-tile {
          font-size: var(--yn-tile-fs);
          overflow: hidden;
        }
        .pearson-dnd-yn-tray {
          width: calc(var(--dz-w) + 2 * ${T.ynTrayPadX}px);
          flex: 0 0 calc(var(--dz-w) + 2 * ${T.ynTrayPadX}px);
          box-sizing: border-box;
        }
        .pearson-dnd-yn-statement {
          flex: 0 0 var(--yn-stmt-w);
          width: var(--yn-stmt-w);
          max-width: var(--yn-stmt-w);
          min-width: 0;
          min-height: var(--dz-h);
          box-sizing: border-box;
          font-size: var(--yn-stmt-fs);
          line-height: var(--yn-stmt-lh);
          overflow-wrap: anywhere;
          word-break: break-word;
        }
      `}</style>
      <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', gap: T.ynRowGap, minWidth: 0 }}>
        {list.map((item) => {
          const filled = isFilled(item.id);
          const val = filled ? valueOf(item.id) : null;
          const correctness = readOnly && filled ? getCorrectness(item.id) : null;
          const stateLabel = filled ? (val ? 'Yes' : 'No') : 'not answered';
          return (
            <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: T.ynStatementGap }}>
              <div
                className="pearson-dnd-yn-statement mathjax-process tex2jax_process"
                style={{
                  minWidth: 0,
                  overflowWrap: 'anywhere',
                  wordBreak: 'break-word',
                  background: '#FFFFFF',
                  border: '1px solid #000000',
                  padding: '10px 12px',
                  textAlign: 'center',
                  fontFamily: T.fontUi,
                  color: '#000000',
                }}
                dangerouslySetInnerHTML={{ __html: typeof prepareExamHtml === 'function' ? prepareExamHtml(item.text || '') : (item.text || '') }}
              />
              <div
                ref={(el) => { boxRefs.current[item.id] = el; }}
                data-yn-dropzone-item={item.id}
                className="pearson-dnd-yn-dropzone"
                aria-label={`${(item.text || '').replace(/<[^>]*>/g, '')}: ${stateLabel}`}
                style={{
                  border: '1px solid #000000',
                  background: filled ? '#FFFFFF' : dropzoneEmptyBg,
                  touchAction: readOnly ? undefined : 'none',
                }}
              >
                {filled ? renderTile(val, { source: 'box', fromItemId: item.id, correctness }) : null}
              </div>
            </div>
          );
        })}
      </div>
      {!readOnly ? (
        <div
          className="pearson-dnd-yn-tray"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 18,
            alignSelf: trayAlignRows ? 'flex-start' : 'center',
            marginTop: trayAlignRows ? T.ynDropzoneHeight + T.ynRowGap : 0,
            background: trayBg,
            padding: T.ynTrayPadX,
          }}
        >
          {renderTile(true, { key: 'tray-yes', source: 'tray', correctness: null, ariaLabel: 'Yes tile, draggable' })}
          {renderTile(false, { key: 'tray-no', source: 'tray', correctness: null, ariaLabel: 'No tile, draggable' })}
        </div>
      ) : null}
      {ghost ? createPortal(
        <div
          aria-hidden="true"
          data-testid="pearson-yn-ghost"
          style={{
            position: 'fixed',
            left: `${ghost.x}px`,
            top: `${ghost.y}px`,
            transform: ghost.transform,
            width: ghost.width,
            height: ghost.height,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: T.ynGhostFill,
            opacity: 1,
            border: `2px solid ${T.ynGhostOutline}`,
            color: T.ynGhostText,
            fontFamily: T.fontUi,
            fontSize: 16,
            boxSizing: 'border-box',
            pointerEvents: 'none',
            zIndex: T.ynGhostZIndex,
          }}
        >
          {ghost.val ? 'Yes' : 'No'}
        </div>,
        document.body
      ) : null}
    </div>
  );
}
